import type { DraggableAttributes } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import type { Column, RowData, TableFeatures } from '@tanstack/react-table';
import {
  type DragEvent,
  type MouseEvent,
  type RefObject,
  type TouchEvent,
  useRef,
} from 'react';

import { isDragMotion } from '../features/ColumnOrder/column-order-dnd-utils.js';

interface UseHeaderCellDragAndDropParams<
  TFeatures extends TableFeatures,
  TData extends RowData,
> {
  /**
   * Unique identifier for the header cell.
   */
  headerId: string;
  /**
   * Column instanceassociated with the header cell.
   */
  column: Column<TFeatures, TData>;
  /**
   * Index of the column based on the virtualization index.
   */
  columnIndex: number;
  /**
   * Mode for column drag-and-drop functionality.
   */
  columnDragAndDropMode: 'none' | 'enabled' | 'only';
}

interface UseHeaderCellDragAndDropResult {
  /**
   * Ref to set on the draggable element.
   */
  setNodeRef: (node: HTMLElement | null) => void;

  /**
   * Whether the header is currently being dragged.
   */
  isDragging: boolean;

  /**
   * Ref for the portal container when dragging.
   */
  portalRef: RefObject<HTMLElement | null>;

  /**
   * Props to spread on the draggable element (undefined if drag-and-drop is disabled).
   */
  dragAndDropProps:
    | ({
        /**
         * Indicates that the element is draggable.
         */
        draggable: boolean;
        /**
         * Event handler for the mouse down event. Tracks which element received the
         * mousedown so that onDragStart can ignore drags initiated from child controls
         * (e.g. the resize handle).
         */
        onMouseDown: (event: MouseEvent<HTMLElement>) => void;
        /**
         * Event handler for the drag start event.
         */
        onDragStart: (event: DragEvent<HTMLElement>) => void;
        /**
         * Event handler for the touch start event.
         */
        onTouchStart: (event: TouchEvent<HTMLElement>) => void;
        /**
         * Event handler for the touch move event.
         */
        onTouchMove: (event: TouchEvent<HTMLElement>) => void;
      } & DraggableAttributes)
    | undefined;
}

/**
 * Hook that encapsulates drag-and-drop functionality for header cells.
 *
 * @param params - Configuration for drag-and-drop behavior
 * @returns Drag-and-drop state and props to spread on the header element
 * @internal
 */
export function useHeaderCellDragAndDrop<
  TFeatures extends TableFeatures,
  TData extends RowData,
>({
  headerId,
  column,
  columnIndex,
  columnDragAndDropMode,
}: UseHeaderCellDragAndDropParams<
  TFeatures,
  TData
>): UseHeaderCellDragAndDropResult {
  const touchStart = useRef<TouchEvent>();
  const portalRef = useRef<HTMLElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  const { setNodeRef, isDragging, attributes, listeners } = useSortable({
    id: headerId,
    data: {
      portalRef,
      column,
      columnIndex,
    },
  });

  const dragAndDropProps =
    columnDragAndDropMode === 'none'
      ? undefined
      : {
          ...attributes,
          draggable: true,
          onMouseDown: (event: MouseEvent<HTMLElement>) => {
            mouseDownTarget.current = event.target;
          },
          onDragStart: (event: DragEvent<HTMLElement>) => {
            // Firefox fires dragstart on a draggable ancestor even when the pointer went down on a
            // non-draggable child (e.g. the resize handle). Skip column reorder activation in that case.
            const isResizing =
              mouseDownTarget.current instanceof HTMLElement &&
              mouseDownTarget.current.closest('[data-trigger="resize"]') !==
                null;

            if (isResizing) {
              event.preventDefault();
            }
            listeners?.onMouseDown(event);
          },
          onTouchStart: (event: TouchEvent<HTMLElement>) => {
            touchStart.current = event;
          },
          onTouchMove: (event: TouchEvent<HTMLElement>) => {
            if (isDragMotion(event, touchStart?.current)) {
              listeners?.onTouchStart(event);
            }
          },
        };

  return {
    setNodeRef,
    isDragging,
    portalRef,
    dragAndDropProps,
  };
}
